import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import {
  ChevronLeft, ChevronRight, Copy, Check, Sparkles, Loader2,
  Monitor, Instagram, Pin, Pencil, Eye, RefreshCw, Save
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { InstagramPostView, InstagramFeedView } from './InstagramPreview';
import PinterestPinView from './PinterestPinView';
import axios from 'axios';
import { checkExportEntitlement, injectExportBranding } from '../utils/exportEntitlement';

import { themes, sizeConfigs } from './product-view/themes';
import ThemeSelector from './product-view/ThemeSelector';
import ExportPanel from './product-view/ExportPanel';
import SaveTemplateModal from './product-view/SaveTemplateModal';
import VisualSlide from './product-view/VisualSlide';
import CoverSlide from './product-view/CoverSlide';
import SlideEditPanel from './product-view/SlideEditPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISUAL_TEMPLATE_CATEGORIES = ['carousel', 'educational', 'marketing', 'tips', 'storytelling', 'other'];

const VisualProductView = ({ content, onSave, onNew, saving, onUpdateContent, onEditSettings, projectId, outputSize, readOnly = false, productSlug = null }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState('modern-minimal');
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState('design');
  const [editMode, setEditMode] = useState(false);
  const [regeneratingSlide, setRegeneratingSlide] = useState(null);
  const [savingChanges, setSavingChanges] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const slideRef = useRef(null);

  if (!content) return null;

  const slides = content.slides || [];
  const allSlides = [{ type: 'cover', headline: content.title, subtext: '' }, ...slides];
  const totalDisplay = allSlides.length;
  const sizeClass = sizeConfigs[outputSize]?.aspect || 'aspect-square';

  const handlePrev = () => setCurrentSlide(prev => Math.max(0, prev - 1));
  const handleNext = () => setCurrentSlide(prev => Math.min(totalDisplay - 1, prev + 1));

  /* ─── Edit functions ─── */
  const handleSlideUpdate = (slideIndex, updatedSlide) => {
    const newSlides = [...slides];
    newSlides[slideIndex] = updatedSlide;
    onUpdateContent({ ...content, slides: newSlides });
    setHasUnsavedChanges(true);
  };

  const handleDeleteSlide = (slideIndex) => {
    const newSlides = slides.filter((_, i) => i !== slideIndex);
    onUpdateContent({ ...content, slides: newSlides });
    setHasUnsavedChanges(true);
    if (currentSlide > newSlides.length) setCurrentSlide(newSlides.length);
  };

  const handleRegenerateSlide = async (slideIndex) => {
    setRegeneratingSlide(slideIndex);
    try {
      const res = await axios.post(`${API}/content/regenerate-slide`, {
        product_title: content.title, slide_index: slideIndex,
        existing_slide: slides[slideIndex], context_summary: content.caption || '',
        language: 'en', tone: 'friendly'
      });
      handleSlideUpdate(slideIndex, res.data);
    } catch (err) { console.error('Regenerate slide failed:', err); }
    setRegeneratingSlide(null);
  };

  const handleSaveChanges = async () => {
    if (!projectId) return;
    setSavingChanges(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, { content, title: content.title });
      setHasUnsavedChanges(false);
    } catch (err) { console.error('Save changes failed:', err); }
    setSavingChanges(false);
  };

  const handleCopyCaption = () => {
    if (content.caption) {
      navigator.clipboard.writeText(`${content.caption}\n\n${content.hashtags || ''}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /* ─── Export ─── */
  const doExport = async (format) => {
    setExporting(true);
    setExportProgress(0);
    const prevMode = viewMode;
    setViewMode('design');
    await new Promise(r => setTimeout(r, 100));

    // Server-side entitlement check
    const entitlement = await checkExportEntitlement(productSlug);
    const shouldBrand = !entitlement.clean;

    try {
      const cfg = sizeConfigs[outputSize] || sizeConfigs['instagram-post'];
      const isPDF = format === 'pdf';
      const pdf = isPDF ? new jsPDF({ orientation: cfg.height > cfg.width ? 'portrait' : 'landscape', unit: 'px', format: [cfg.width, cfg.height], compress: true }) : null;

      for (let i = 0; i < totalDisplay; i++) {
        setCurrentSlide(i);
        setExportProgress(Math.round(((i + 1) / totalDisplay) * 100));
        await new Promise(r => setTimeout(r, 300));
        const el = slideRef.current;
        if (!el) continue;

        // Inject branding for free users
        let removeBranding = null;
        if (shouldBrand) {
          removeBranding = injectExportBranding(el);
          await new Promise(r => setTimeout(r, 50));
        }

        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });

        // Remove branding immediately after capture
        if (removeBranding) removeBranding();

        if (isPDF) {
          const targetW = cfg.width * 2;
          const targetH = cfg.height * 2;
          let finalCanvas = canvas;
          if (canvas.width > targetW || canvas.height > targetH) {
            const offscreen = document.createElement('canvas');
            offscreen.width = targetW;
            offscreen.height = targetH;
            const ctx = offscreen.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(canvas, 0, 0, targetW, targetH);
            finalCanvas = offscreen;
          }
          const imgData = finalCanvas.toDataURL('image/jpeg', 0.85);
          if (i > 0) pdf.addPage([cfg.width, cfg.height]);
          pdf.addImage(imgData, 'JPEG', 0, 0, cfg.width, cfg.height, undefined, 'FAST');
        } else {
          const link = document.createElement('a');
          link.download = `${content.title || 'slide'}-${i + 1}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      }
      if (isPDF) pdf.save(`${content.title || 'product'}.pdf`);
    } catch (err) { console.error(`${format.toUpperCase()} export error:`, err); }
    setViewMode(prevMode);
    setExporting(false);
  };

  const renderSlideByIndex = (index) => {
    if (index === 0) return <CoverSlide title={content.title} subtitle={content.slides?.[0]?.subtext || ''} theme={selectedTheme} />;
    return <VisualSlide slide={allSlides[index]} index={index} total={totalDisplay - 1} theme={selectedTheme} />;
  };

  const current = allSlides[currentSlide];

  return (
    <div data-testid="visual-product-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900">
            {readOnly ? content.title || 'Shared product' : 'Your product is ready!'}
          </h2>
          {!readOnly && <p className="text-stone-600 font-sans mt-1">Preview, edit, customize the theme and export.</p>}
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center bg-stone-100 rounded-full p-1">
              <button data-testid="carousel-preview-toggle" onClick={() => setEditMode(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-semibold transition-all ${!editMode ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                <Eye className="w-3.5 h-3.5" /> Preview
              </button>
              <button data-testid="carousel-edit-toggle" onClick={() => { setEditMode(true); setViewMode('design'); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-semibold transition-all ${editMode ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            {hasUnsavedChanges && projectId && (
              <Button data-testid="carousel-save-changes-btn" onClick={handleSaveChanges} disabled={savingChanges} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4 h-9 text-xs font-sans font-semibold">
                {savingChanges ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save Changes
              </Button>
            )}
            <Button data-testid="save-project-btn" onClick={onSave} disabled={saving} className="bg-accent text-white hover:bg-accent/90 rounded-full px-5 font-sans font-semibold h-9 text-xs">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
            </Button>
            <Button data-testid="carousel-save-template-btn" onClick={() => setShowSaveTemplate(true)} variant="outline" className="rounded-full px-4 h-9 text-xs font-sans font-semibold border-stone-200 text-stone-600 hover:border-primary hover:text-primary">
              <Sparkles className="w-3 h-3 mr-1" /> Save as template
            </Button>
            <Button data-testid="visual-new-btn" variant="ghost" onClick={onNew} className="text-stone-600 font-sans h-9 text-xs">Lag ny</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Preview Area */}
        <div className="lg:col-span-2 space-y-4">
          {/* View mode toggle + Theme selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div data-testid="view-mode-toggle" className="inline-flex items-center bg-stone-100 rounded-full p-1 overflow-x-auto max-w-full">
              {[
                { key: 'design', icon: Monitor, label: 'Design' },
                { key: 'post', icon: Instagram, label: 'Post View' },
                { key: 'feed', icon: null, label: 'Feed' },
                { key: 'pins', icon: Pin, label: 'Pins' },
              ].map(({ key, icon: Icon, label }) => (
                <button key={key} data-testid={`view-${key}`} onClick={() => setViewMode(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-sans font-semibold transition-all ${viewMode === key ? 'bg-white shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}>
                  {Icon ? <Icon className="w-3.5 h-3.5" /> : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
                    </svg>
                  )}
                  {label}
                </button>
              ))}
            </div>

            {viewMode === 'design' && (
              <div className="flex items-center gap-2">
                <ThemeSelector selectedTheme={selectedTheme} onSelectTheme={setSelectedTheme} />
                {onEditSettings && (
                  <Button data-testid="carousel-edit-settings-btn" onClick={onEditSettings} variant="outline" className="h-7 px-3 text-[11px] font-sans font-semibold rounded-full border-stone-200 text-stone-600 hover:border-primary hover:text-primary ml-1">
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerate All
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* ─── DESIGN VIEW ─── */}
          {viewMode === 'design' && (
            <>
              <div ref={slideRef} className={`w-full max-w-[540px] mx-auto rounded-2xl overflow-hidden shadow-lg ${sizeClass}`}>
                {currentSlide === 0
                  ? <CoverSlide title={content.title} subtitle={content.slides?.[0]?.subtext || ''} theme={selectedTheme} sizeClass={sizeClass} />
                  : <VisualSlide slide={current} index={currentSlide} total={totalDisplay - 1} theme={selectedTheme} sizeClass={sizeClass} />}
              </div>

              <div className="flex items-center justify-center gap-4">
                <Button variant="ghost" onClick={handlePrev} disabled={currentSlide === 0} className="text-stone-500 rounded-full w-10 h-10 p-0"><ChevronLeft className="w-5 h-5" /></Button>
                <span className="text-sm font-sans font-medium text-stone-500">{currentSlide === 0 ? 'Cover' : `Slide ${currentSlide}`} / {totalDisplay - 1} slides</span>
                <Button variant="ghost" onClick={handleNext} disabled={currentSlide === totalDisplay - 1} className="text-stone-500 rounded-full w-10 h-10 p-0"><ChevronRight className="w-5 h-5" /></Button>
              </div>

              <div className="flex items-center gap-2 overflow-x-auto pb-2 px-1">
                {allSlides.map((s, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg border-2 overflow-hidden transition-all ${currentSlide === i ? 'border-primary scale-110 shadow-md' : 'border-stone-200 opacity-60 hover:opacity-100'}`}>
                    <div className="w-full h-full flex items-center justify-center text-[8px] font-bold"
                      style={{
                        backgroundColor: (i === 0 || s.type === 'hook' || s.type === 'cta') ? themes[selectedTheme].accent : themes[selectedTheme].bg,
                        color: (i === 0 || s.type === 'hook' || s.type === 'cta') ? themes[selectedTheme].bg : themes[selectedTheme].text
                      }}>
                      {i === 0 ? 'Cover' : i}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Edit panels */}
          {editMode && viewMode === 'design' && currentSlide > 0 && slides[currentSlide - 1] && (
            <SlideEditPanel
              slide={slides[currentSlide - 1]} slideIndex={currentSlide - 1}
              onUpdate={handleSlideUpdate} onRegenerate={handleRegenerateSlide}
              onDelete={handleDeleteSlide} regenerating={regeneratingSlide === currentSlide - 1}
              totalSlides={slides.length}
            />
          )}
          {editMode && viewMode === 'design' && currentSlide === 0 && (
            <div className="bg-white rounded-2xl border border-stone-100 p-5 space-y-4">
              <h4 className="font-sans font-bold text-stone-800 text-sm">Edit cover</h4>
              <div>
                <label className="text-xs font-sans font-medium text-stone-500 mb-1 block">Title</label>
                <input data-testid="edit-carousel-title" value={content.title || ''}
                  onChange={(e) => { onUpdateContent({ ...content, title: e.target.value }); setHasUnsavedChanges(true); }}
                  className="w-full text-sm font-sans font-semibold text-stone-800 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none" />
              </div>
            </div>
          )}

          {/* Other views */}
          {viewMode === 'post' && (
            <InstagramPostView slides={slides} allSlides={allSlides} caption={content.caption} hashtags={content.hashtags} theme={selectedTheme} themes={themes} renderSlide={renderSlideByIndex} />
          )}
          {viewMode === 'feed' && (
            <InstagramFeedView allSlides={allSlides} theme={selectedTheme} themes={themes} renderSlide={renderSlideByIndex} />
          )}
          {viewMode === 'pins' && <PinterestPinView content={content} />}
        </div>

        {/* Right: Export & Caption */}
        {!readOnly && (
          <>
            <div className="space-y-6">
              <ExportPanel
                onExportPNG={() => doExport('png')} onExportPDF={() => doExport('pdf')}
                exporting={exporting} exportProgress={exportProgress}
                totalCount={totalDisplay} outputSize={outputSize}
                variant="visual"
                productTitle={content.title || content.cover?.title || ''}
                productDescription={content.caption || content.cover?.subtitle || ''}
                productType="Visual Content"
                productSlug={productSlug}
                projectId={projectId}
                themeId={selectedTheme}
              />

              {content.caption && (
                <div className="bg-white rounded-2xl border border-stone-100 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-sans font-bold text-stone-800">Bildetekst</h3>
                    <Button data-testid="copy-caption-btn" variant="ghost" onClick={handleCopyCaption} className="text-xs font-sans font-semibold text-primary h-auto py-1 px-2">
                      {copied ? <><Check className="w-3 h-3 mr-1" /> Copyt</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                    </Button>
                  </div>
                  <p className="text-sm font-sans text-stone-600 whitespace-pre-line leading-relaxed max-h-48 overflow-y-auto">{content.caption}</p>
                  {content.hashtags && <p className="text-xs font-sans text-primary mt-3 leading-relaxed">{content.hashtags}</p>}
                </div>
              )}

              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <h3 className="font-sans font-bold text-stone-800 mb-3">All slides</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {slides.map((slide, i) => (
                    <button key={i} onClick={() => { setViewMode('design'); setCurrentSlide(i + 1); }}
                      className={`w-full text-left p-3 rounded-lg border transition-all text-sm ${viewMode === 'design' && currentSlide === i + 1 ? 'border-primary bg-primary/5' : 'border-stone-100 hover:border-stone-200'}`}>
                      <span className="text-[10px] font-bold text-primary uppercase">{slide.type}</span>
                      <p className="font-sans font-medium text-stone-700 leading-snug mt-0.5">{slide.headline}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <SaveTemplateModal
              open={showSaveTemplate}
              onClose={() => setShowSaveTemplate(false)}
              content={content}
              productType="carousel"
              categories={VISUAL_TEMPLATE_CATEGORIES}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default VisualProductView;
